import React, { useEffect, useState } from 'react'
import { Box, Typography, Stack, Button, TextField, Divider, Drawer, Autocomplete } from '@mui/material'
import PropTypes from 'prop-types'
import { useConversionReport } from 'hooks/useConversionReport'
import useAdInsightsReport from 'queries/use-ad-insights-report'
import { statuses } from './constants'
import { initialState as initialStateConversionReport } from 'contexts/conversion-report/reducer'

function FilterDrawer({ open, toggleDrawer }) {
  const {
    state: { adAccountId, date, search, campaigns: filteredCampaigns, ads: filteredAds, statuses: filteredStatuses },
    actions: { setStatuses, setCampaigns, setAds, setPagination },
  } = useConversionReport()

  const isAdvertisement = search === 'ad_id'

  const [selectedCampaigns, setSelectedCampaigns] = useState([])
  const [selectedPlacements, setSelectedPlacements] = useState([])
  const [selectedAds, setSelectedAd] = useState([])

  const { data: campaigns } = useAdInsightsReport({
    accountId: adAccountId,
    startDate: date.start,
    endDate: date.end,
    groupBy: 'campaign',
    campaigns: [],
    ads: [],
    statuses: [],
    page: 1,
    perPage: 100,
  })

  const { data: ads } = useAdInsightsReport({
    accountId: adAccountId,
    startDate: date.start,
    endDate: date.end,
    groupBy: 'ad_id',
    campaigns: selectedCampaigns.length && selectedCampaigns.map((camp) => camp?.CampaignId) || [],
    statuses: [],
    ads: [],
    page: 1,
    perPage: 100,
  })

  useEffect(() => {
    if(campaigns){
      setSelectedCampaigns([...filteredCampaigns.map((camp) => campaigns?.Results?.find((c) => c.CampaignId === camp))])
    }
    if (ads) {
      setSelectedAd([...filteredAds.map((ad) => ads?.Results?.find((a) => a.AdId === ad))])
    }
    setSelectedPlacements([...filteredStatuses.map((status) => statuses.find((s) => s.id === status))])
  }, [campaigns])

  const handleCampaignsChange = (_, newValue) => {
    if (newValue.length === 0) {
      setSelectedCampaigns([])
    }

    setSelectedCampaigns(newValue)
    if (isAdvertisement) setSelectedAd([])
  }

  const handlePlacementChange = (_, newValue) => {
    if (newValue.length === 0) {
      setSelectedPlacements([])
    }
    setSelectedPlacements(newValue)
  }

  const handleAdChange = (_, newValue) => {
    if (newValue.length === 0) {
      setSelectedAd([])
    }
    setSelectedAd(newValue)
  }

  const handleClearSelectedFilters = () => {
    setSelectedAd([])
    setSelectedPlacements([])
    setSelectedCampaigns([])

    setStatuses([])
    setCampaigns([])
    setAds([])
  }

  const handleApplyFilter = () => {
    setStatuses(selectedPlacements.map((placement) => placement.id))
    setCampaigns(selectedCampaigns.map((camp) => camp.CampaignId))
    setAds(selectedAds.map((ad) => ad.AdId))
    setPagination({current: initialStateConversionReport.page.current, size: initialStateConversionReport.page.size})

    toggleDrawer()
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer}
      onOpen={toggleDrawer}
      data-testid="swiapeable-drawer"
      sx={{
        '& .HiTheme-Drawer-paper': {
          padding: '1.5rem',
          width: '22rem',
        },
      }}
      ModalProps={{
        disableAutoFocus: true,
        disableAutoFocusItem: true,
        disableEnforceFocus: true,
        disableEnforceFocusItem: true,
      }}
    >
      <Stack flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="500" color="#666666" padding="0 12px">
          Filtrar por:
        </Typography>
        <Autocomplete
          disablePortal
          data-testid="campaign-field-autocomplete"
          fullWidth
          multiple
          limitTags={1}
          id="campaigns-filter"
          options={campaigns?.Results || []}
          getOptionLabel={(option) => option?.CampaignName}
          disableCloseOnSelect
          value={selectedCampaigns && selectedCampaigns.length > 0 ? selectedCampaigns : []}
          ChipProps={{
            'data-id': 'chip-filter',
          }}
          renderInput={(params) => (
            <TextField
              style={{ color: '#00aeef' }}
              {...params}
              variant="outlined"
              sx={{
                '& .HiTheme-OutlinedInput-notchedOutline': {
                  border: 'none',
                  borderBottom: '1px solid #0000003b',
                  borderRadius: '0px',
                  margin: '0px 10px',
                },
              }}
              data-id="form-field-campaign"
              label="Campanha"
              placeholder="Selecionar"
            />
          )}
          noOptionsText="Nenhum resultado"
          onChange={handleCampaignsChange}
        />

        {isAdvertisement && (
          <Autocomplete
            disablePortal
            data-testid="ads-field-autocomplete"
            fullWidth
            multiple
            limitTags={1}
            id="ad-filter"
            options={ads?.Results || []}
            getOptionLabel={(option) => option.Name}
            disableCloseOnSelect
            value={selectedAds?.length > 0 ? selectedAds : []}
            ChipProps={{
              'data-id': 'chip-filter',
            }}
            renderInput={(params) => (
              <TextField
                style={{ color: '#00aeef' }}
                {...params}
                variant="outlined"
                sx={{
                  '& .HiTheme-OutlinedInput-notchedOutline': {
                    border: 'none',
                    borderBottom: '1px solid #0000003b',
                    borderRadius: '0px',
                    margin: '0px 10px',
                  },
                }}
                data-id="form-field-ads"
                label="Anúncio"
                placeholder="Selecionar"
              />
            )}
            noOptionsText="Nenhum resultado"
            onChange={handleAdChange}
          />
        )}

        <Autocomplete
          disablePortal
          data-testid="placement-field-autocomplete"
          fullWidth
          multiple
          limitTags={1}
          id="placements-filter"
          options={statuses}
          disableCloseOnSelect
          value={selectedPlacements && selectedPlacements.length > 0 ? selectedPlacements : []}
          ChipProps={{
            'data-id': 'chip-filter',
          }}
          renderInput={(params) => (
            <TextField
              style={{ color: '#00aeef' }}
              {...params}
              variant="outlined"
              sx={{
                '& .HiTheme-OutlinedInput-notchedOutline': {
                  border: 'none',
                  borderBottom: '1px solid #0000003b',
                  borderRadius: '0px',
                  margin: '0px 10px',
                },
              }}
              data-id="form-field-placement"
              label="Status de Veiculação"
              placeholder="Selecionar"
            />
          )}
          noOptionsText="Nenhum resultado"
          onChange={handlePlacementChange}
        />
        <Divider sx={{ margin: '30px -24px 10px -24px', backgroundColor: '#cacaca' }} />
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="text"
            sx={{ color: '#00aeef' }}
            onClick={() => {
              handleClearSelectedFilters()
            }}
            data-testid="reset-btn-action"
          >
            Redefinir
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#00aeef',
              '&:hover': {
                backgroundColor: '#009deb',
              },
            }}
            onClick={() => {
              handleApplyFilter()
            }}
            data-testid="apply-btn-action"
          >
            Aplicar
          </Button>
        </Box>
      </Stack>
    </Drawer>
  )
}

FilterDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
}

export default FilterDrawer
